import { Head, usePage } from "@inertiajs/react";

import { FinalCta } from "@/components/final-cta";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import type { GalleryImageSummary } from "@/types";

interface GalleryItem {
  src: string;
  alt: string;
  brand?: string;
  template?: string;
}

function GalleryGrid({ items }: { items: GalleryItem[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((item, index) => (
        <div
          key={index}
          className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:shadow-xl dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="aspect-[1.91/1] w-full overflow-hidden">
            <img
              src={item.src}
              alt={item.alt}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              fetchPriority={index < 4 ? "high" : "low"}
              decoding="async"
            />
          </div>
          {(item.brand || item.template) && (
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 via-black/50 to-transparent p-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              {item.brand && <span className="text-xs font-semibold text-white">{item.brand}</span>}
              {item.template && (
                <span className="rounded bg-white/20 px-2 py-1 text-xs text-white backdrop-blur-sm">
                  {item.template}
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Gallery() {
  const { flags, gallery_images = [] } = usePage<{ gallery_images?: GalleryImageSummary[] }>().props;
  const items: GalleryItem[] = Array.isArray(gallery_images)
    ? gallery_images.map((image) => ({
        src: image.src,
        alt: image.alt,
        brand: image.brand ?? undefined,
        template: image.template ?? undefined,
      }))
    : [];

  return (
    <>
      <Head title="The OG Pilot Gallery" />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <PublicHeader />

        {/* Hero Section */}
        <section className="container mx-auto max-w-7xl px-4 pt-12 pb-8">
          <div className="text-center">
            <h1 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl lg:text-6xl dark:text-white">
              The OG Pilot Gallery
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-balance text-gray-600 md:text-xl dark:text-gray-300">
              Discover how leading brands can transform their social previews with generated images. Every image here
              was created by OG Pilot in seconds.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-orange-500"></div>
                <span>60+ Real Examples</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                <span>Multiple Templates</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                <span>Brand Optimized</span>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="container mx-auto max-w-7xl px-4 pb-20">
          <GalleryGrid items={items} />
        </section>

        <FinalCta
          title="Marketers, turn gallery inspiration into client-ready previews"
          description="Plug OG Pilot into your client stack, auto-generate branded OG images, and prove the lift with analytics. No designer handoffs, no last-minute scrambles—just scroll-stopping visuals that win retainers."
          highlights={[
            { icon: "⚡️", text: "Unlimited refinements for campaigns" },
            { icon: "📊", text: "Client-ready performance reports" },
          ]}
          submitLabel={flags.free_one_off_generation ? "Start your free trial" : "Try OG Pilot"}
        />
      </div>
      <SiteFooter />
    </>
  );
}
